import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { formatIndianDate } from '../utils/formatters';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Search, Wrench, Check, ChevronsUpDown, Eye, AlertTriangle, Clock, MessageSquare, Edit, CheckCircle, XCircle, UserCog, Image, Phone, Download } from 'lucide-react';
import useProtectedExport from '../hooks/useProtectedExport';
import ExportPasswordDialog from '../components/ExportPasswordDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EXPORT_ALLOWED_ROLES = ["Admin"];

const ServiceManager = () => {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [pendingApprovalServices, setPendingApprovalServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [updateStatusDialogOpen, setUpdateStatusDialogOpen] = useState(false);
  const [closureDialogOpen, setClosureDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [engineerFilter, setEngineerFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [expiredOnly, setExpiredOnly] = useState(false);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerContracts, setCustomerContracts] = useState([]);
  const [customerLicenses, setCustomerLicenses] = useState([]);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelNotes, setCancelNotes] = useState('');
  const [changeEngineerDialogOpen, setChangeEngineerDialogOpen] = useState(false);
  const [newEngineerId, setNewEngineerId] = useState('');
  const [changeEngineerReason, setChangeEngineerReason] = useState('');
  
  // Role checks
  const isAdmin = user?.role === 'Admin';
  const isBackOffice = user?.role === 'Back Office Executive';
  const isBackOfficeAccounts = user?.role === 'Backoffice Accounts Executive';
  const isBDMSE = user?.role === 'BDM & SE';
  const isServiceEngineer = user?.role === 'Service Engineer';
  
  // Check if user can export
  const canExport = EXPORT_ALLOWED_ROLES.includes(user?.role);
  const protectedExport = useProtectedExport();
  
  // Export CSV handler
  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('service_type', typeFilter);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/services/export/csv?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `services_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };
  
  // Check if user can edit a specific service
  // BDM & SE and Service Engineer can only edit their assigned services
  const canEditService = (service) => {
    if (isAdmin || isBackOffice || isBackOfficeAccounts) return true;
    if (isBDMSE || isServiceEngineer) {
      return service.assigned_to === user?.id;
    }
    return false;
  };
  
  const [formData, setFormData] = useState({
    customer_id: '',
    service_type: '',
    description: '',
    priority: 'Normal',
    due_date: '',
    due_time: '',
    assigned_to: '',
    contract_id: '',
    license_id: '',
    device_reference_number: ''
  });

  // Closure form data - NEW WORKFLOW
  const [closureData, setClosureData] = useState({
    service_type_category: '', // "Free" or "Paid"
    payment_status: '', // "Paid" or "Pending" (only if Paid service)
    payment_mode: '', // Cash, UPI, Bank Transfer, Cheque
    payment_notes: '',
    free_service_notes: '',
    service_charge: '',
    due_date: '',
    collected_by: ''
  });

  // Approval form data
  const [approvalData, setApprovalData] = useState({
    approval_notes: '',
    payment_reference: '',
    action: 'approve' // or 'reject'
  });

  // Comment form data
  const [commentData, setCommentData] = useState({
    comment: '',
    next_visit_date: ''
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 250;

  // Define fetch functions with useCallback BEFORE using them in useEffect
  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (engineerFilter && engineerFilter !== 'all') params.assigned_to = engineerFilter;
      if (expiredOnly) params.expired = true;
      
      const response = await api.get('/services/', { params });
      const sortedServices = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setServices(sortedServices);
    } catch (error) {
      toast.error('Failed to fetch services');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, engineerFilter, expiredOnly]);

  const fetchPendingApprovalServices = useCallback(async () => {
    try {
      const response = await api.get('/services/pending-approval');
      setPendingApprovalServices(response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await api.get('/customers/');
      setCustomers(response.data.filter(c => c.status === 'Active'));
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchEngineers = useCallback(async () => {
    try {
      const response = await api.get('/services/engineers/list');
      setEngineers(response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchServiceTypes = useCallback(async () => {
    try {
      const response = await api.get('/services/types');
      setServiceTypes(response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  // Set default date filter: 30 days before to 30 days after current date (using IST timezone)
  useEffect(() => {
    // Use IST timezone for date calculation
    const getISTDate = () => {
      const now = new Date();
      // Convert to IST (UTC+5:30)
      const istOffset = 5.5 * 60 * 60 * 1000;
      const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
      const istTime = new Date(utcTime + istOffset);
      return istTime;
    };
    
    const today = getISTDate();
    const thirtyDaysBefore = new Date(today);
    thirtyDaysBefore.setDate(today.getDate() - 30);
    const thirtyDaysAfter = new Date(today);
    thirtyDaysAfter.setDate(today.getDate() + 30);
    
    setFromDate(thirtyDaysBefore.toISOString().split('T')[0]);
    setToDate(thirtyDaysAfter.toISOString().split('T')[0]);
    
    // Default engineer filter for BDM & SE and Service Engineer to show their own services
    if ((isBDMSE || isServiceEngineer) && user?.id) {
      setEngineerFilter(user.id);
    }
  }, [isBDMSE, isServiceEngineer, user?.id]);

  useEffect(() => {
    if (fromDate && toDate) {
      fetchServices();
    }
    fetchCustomers();
    fetchEngineers();
    fetchServiceTypes();
    if (user?.role === 'Admin' || user?.role === 'Back Office Executive' || user?.role === 'Backoffice Accounts Executive') {
      fetchPendingApprovalServices();
    }
  }, [user, fromDate, toDate, engineerFilter, fetchServices, fetchCustomers, fetchEngineers, fetchServiceTypes, fetchPendingApprovalServices]);

  const fetchCustomerContracts = async (customerId) => {
    try {
      const response = await api.get(`/services/customer/${customerId}/contracts`);
      setCustomerContracts(response.data);
    } catch (error) {
      console.error(error);
      setCustomerContracts([]);
    }
  };

  const fetchCustomerLicenses = async (customerId) => {
    try {
      const response = await api.get(`/services/customer/${customerId}/licenses`);
      setCustomerLicenses(response.data);
    } catch (error) {
      console.error(error);
      setCustomerLicenses([]);
    }
  };

  const handleOpenDialog = () => {
    const defaultAssignedTo = user?.role === 'Service Engineer' ? user.id : '';
    
    setFormData({
      customer_id: '',
      service_type: '',
      description: '',
      priority: 'Normal',
      due_date: '',
      due_time: '',
      assigned_to: defaultAssignedTo,
      contract_id: '',
      license_id: '',
      device_reference_number: ''
    });
    setCustomerContracts([]);
    setCustomerLicenses([]);
    setCustomerSearchTerm('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCustomerSearchTerm('');
  };

  const handleCustomerSelect = async (customerId) => {
    setFormData(prev => ({ 
      ...prev, 
      customer_id: customerId,
      contract_id: '',
      license_id: ''
    }));
    setCustomerSearchOpen(false);
    
    const customer = customers.find(c => c.id === customerId);
    setCustomerSearchTerm(customer?.company_name || '');
    
    await fetchCustomerContracts(customerId);
    await fetchCustomerLicenses(customerId);
  };

  const handleServiceTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      service_type: value,
      contract_id: '',
      license_id: '',
      device_reference_number: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = { ...formData };
      
      if (submitData.service_type !== 'AMC/MMC Service') {
        delete submitData.contract_id;
      }
      if (submitData.service_type !== 'Stamping/License Renewal Service') {
        delete submitData.license_id;
      }
      if (submitData.service_type !== 'In-Office Service') {
        delete submitData.device_reference_number;
      }
      
      await api.post('/services/', submitData);
      toast.success('Service created successfully');
      handleCloseDialog();
      fetchServices();
    } catch (error) {
      const errorData = error.response?.data;
      let errorMessage = 'Operation failed';
      
      if (errorData?.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        }
      }
      toast.error(errorMessage);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewService = async (service) => {
    try {
      const response = await api.get(`/services/${service.id}`);
      setSelectedService(response.data);
      setViewDialogOpen(true);
      
      // Mark backoffice update as seen if the current user is the assigned engineer
      if (service.backoffice_update_seen === false && service.assigned_to === user?.id) {
        try {
          await api.post(`/services/${service.id}/mark-update-seen`);
          // Update local state to remove the highlight
          setServices(prev => prev.map(s => 
            s.id === service.id ? { ...s, backoffice_update_seen: true } : s
          ));
        } catch (err) {
          console.error('Failed to mark update as seen:', err);
        }
      }
    } catch (error) {
      toast.error('Failed to fetch service details');
    }
  };

  // Status Update
  const handleOpenStatusUpdate = (service) => {
    setSelectedService(service);
    setUpdateStatusDialogOpen(true);
  };

  const handleStatusUpdate = async (newStatus) => {
    if (newStatus === 'Closed') {
      // Open closure dialog for payment details - NEW WORKFLOW
      setUpdateStatusDialogOpen(false);
      setClosureData({
        service_type_category: '',
        payment_status: '',
        payment_mode: '',
        payment_notes: '',
        free_service_notes: '',
        service_charge: '',
        due_date: '',
        collected_by: selectedService?.assigned_to || ''
      });
      setClosureDialogOpen(true);
    } else if (newStatus === 'Canceled') {
      // Open cancel confirmation dialog
      setUpdateStatusDialogOpen(false);
      setCancelNotes('');
      setCancelDialogOpen(true);
    } else {
      // Direct status update
      try {
        await api.put(`/services/${selectedService.id}`, { status: newStatus });
        toast.success(`Service status updated to ${newStatus}`);
        setUpdateStatusDialogOpen(false);
        fetchServices();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update status');
      }
    }
  };

  // Handle Cancel Service with notes
  const handleCancelService = async () => {
    if (!cancelNotes.trim()) {
      toast.error('Cancellation notes are required');
      return;
    }
    
    setLoading(true);
    try {
      await api.put(`/services/${selectedService.id}`, { 
        status: 'Canceled',
        cancellation_notes: cancelNotes
      });
      toast.success('Service cancelled successfully');
      setCancelDialogOpen(false);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel service');
    } finally {
      setLoading(false);
    }
  };

  // Service Closure with NEW WORKFLOW
  const handleClosureSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Build closure data based on new workflow
      const closeData = {
        service_type_category: closureData.service_type_category
      };

      // Free Service
      if (closureData.service_type_category === 'Free') {
        if (!closureData.free_service_notes || closureData.free_service_notes.trim().length < 3) {
          toast.error('Notes are mandatory for free services');
          setLoading(false);
          return;
        }
        closeData.free_service_notes = closureData.free_service_notes;
      }
      // Paid Service
      else if (closureData.service_type_category === 'Paid') {
        if (!closureData.payment_status) {
          toast.error('Payment status is required');
          setLoading(false);
          return;
        }
        if (!closureData.service_charge || parseFloat(closureData.service_charge) <= 0) {
          toast.error('Service charge must be greater than 0');
          setLoading(false);
          return;
        }

        closeData.payment_status = closureData.payment_status;
        closeData.service_charge = parseFloat(closureData.service_charge);
        closeData.payment_notes = closureData.payment_notes || '';

        // If Paid
        if (closureData.payment_status === 'Paid') {
          if (!closureData.payment_mode) {
            toast.error('Payment mode is required');
            setLoading(false);
            return;
          }
          closeData.payment_mode = closureData.payment_mode;
          closeData.collected_by = closureData.collected_by || selectedService?.assigned_to;
        }
        // If Pending
        else if (closureData.payment_status === 'Pending') {
          if (!closureData.due_date) {
            toast.error('Due date is required for pending payments');
            setLoading(false);
            return;
          }
          closeData.due_date = closureData.due_date;
        }
      }

      await api.put(`/services/${selectedService.id}/close`, closeData);
      toast.success('Service closed successfully. Sent for approval.');
      setClosureDialogOpen(false);
      fetchServices();
    } catch (error) {
      const errorData = error.response?.data;
      let errorMessage = 'Failed to close service';
      if (errorData?.detail) {
        errorMessage = typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail);
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Add Comment
  const handleOpenCommentDialog = (service) => {
    setSelectedService(service);
    setCommentData({ comment: '', next_visit_date: '' });
    setCommentDialogOpen(true);
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post(`/services/${selectedService.id}/comments`, commentData);
      toast.success('Comment added successfully');
      setCommentDialogOpen(false);
      fetchServices();
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setLoading(false);
    }
  };

  // Approval
  const handleOpenApprovalDialog = (service, action) => {
    setSelectedService(service);
    setApprovalData({
      approval_notes: '',
      payment_reference: '',
      action: action
    });
    setApprovalDialogOpen(true);
  };

  const handleApprovalSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = approvalData.action === 'approve' 
        ? `/services/${selectedService.id}/approve`
        : `/services/${selectedService.id}/reject`;
      
      const params = new URLSearchParams();
      if (approvalData.action === 'approve') {
        params.append('approval_notes', approvalData.approval_notes);
        if (approvalData.payment_reference) {
          params.append('payment_reference', approvalData.payment_reference);
        }
      } else {
        params.append('rejection_notes', approvalData.approval_notes);
      }

      await api.post(`${endpoint}?${params.toString()}`);
      toast.success(approvalData.action === 'approve' ? 'Service approved successfully' : 'Service rejected and sent back to engineer');
      setApprovalDialogOpen(false);
      fetchServices();
      fetchPendingApprovalServices();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Operation failed';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.company_name?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    customer.contact_person?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    customer.mobile?.includes(customerSearchTerm)
  );

  const filteredServices = services.filter(service => {
    const matchesSearch = 
      service.service_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.customer?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || service.status === statusFilter;
    const matchesType = typeFilter === 'all' || service.service_type === typeFilter;
    const matchesPriority = priorityFilter === 'all' || service.priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesType && matchesPriority;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredServices.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentServices = filteredServices.slice(indexOfFirstRecord, indexOfLastRecord);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, typeFilter, priorityFilter, fromDate, toDate]);

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-800';
      case 'On Process': return 'bg-yellow-100 text-yellow-800';
      case 'Closed': return 'bg-green-100 text-green-800';
      case 'Canceled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Emergency': return 'bg-red-600 text-white font-bold';
      case 'Urgent': return 'bg-orange-500 text-white font-semibold';
      default: return 'bg-green-100 text-green-800';
    }
  };

  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === 'Closed' || status === 'Canceled') return false;
    const today = new Date();
    const due = new Date(dueDate);
    return due < today;
  };

  const canUpdateStatus = (service) => {
    if (service.status === 'Closed' || service.status === 'Canceled') return false;
    // Service Engineer can only update their assigned services
    if (user?.role === 'Service Engineer' && service.assigned_to !== user.id) return false;
    // BDM & SE can only update their assigned services
    if (user?.role === 'BDM & SE' && service.assigned_to !== user.id) return false;
    return true;
  };

  const canApprove = () => {
    return user?.role === 'Admin' || user?.role === 'Back Office Executive' || user?.role === 'Backoffice Accounts Executive';
  };

  const canChangeEngineer = () => {
    return user?.role === 'Admin' || user?.role === 'Back Office Executive' || user?.role === 'Backoffice Accounts Executive';
  };

  const handleChangeEngineer = async () => {
    if (!newEngineerId) {
      toast.error('Please select an engineer');
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.put(
        `/services/${selectedService.id}/change-engineer?new_engineer_id=${newEngineerId}&reason=${encodeURIComponent(changeEngineerReason || '')}`
      );
      toast.success(response.data.message);
      setChangeEngineerDialogOpen(false);
      setNewEngineerId('');
      setChangeEngineerReason('');
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change engineer');
    } finally {
      setLoading(false);
    }
  };

  const openChangeEngineerDialog = (service) => {
    setSelectedService(service);
    setNewEngineerId(service.assigned_to || '');
    setChangeEngineerReason('');
    setChangeEngineerDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Service Management</h1>
          <p className="text-gray-600 mt-1">Manage service bookings and track service status</p>
        </div>
        <div className="flex gap-2">
          {canExport && (
            <Button variant="outline" onClick={() => protectedExport.requestExport(handleExportCSV)} data-testid="export-csv-button">
              <Download size={20} className="mr-2" />
              Export CSV
            </Button>
          )}
          <Button onClick={handleOpenDialog} data-testid="add-service-button">
            <Plus size={20} className="mr-2" />
            Book Service
          </Button>
        </div>
      </div>

      {/* Service List - now same for all roles, approval moved to Sales module */}
      <ServiceListCard 
        services={filteredServices}
        loading={loading}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        engineerFilter={engineerFilter}
        setEngineerFilter={setEngineerFilter}
        priorityFilter={priorityFilter}
        setPriorityFilter={setPriorityFilter}
        expiredOnly={expiredOnly}
        setExpiredOnly={setExpiredOnly}
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
        engineers={engineers}
        serviceTypes={serviceTypes}
        handleViewService={handleViewService}
        handleOpenStatusUpdate={handleOpenStatusUpdate}
        handleOpenCommentDialog={handleOpenCommentDialog}
        handleChangeEngineer={openChangeEngineerDialog}
        canUpdateStatus={canUpdateStatus}
        canChangeEngineer={canChangeEngineer()}
        isOverdue={isOverdue}
        getPriorityColor={getPriorityColor}
        getStatusBadgeVariant={getStatusBadgeVariant}
        user={user}
      />

      {/* Create Service Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Book New Service</DialogTitle>
            <DialogDescription>Create a new service booking</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              {/* Customer Selection */}
              <div className="grid gap-2">
                <Label htmlFor="customer">Customer *</Label>
                <Popover open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={customerSearchOpen}
                      className="justify-between w-full"
                    >
                      {formData.customer_id
                        ? customers.find(c => c.id === formData.customer_id)?.company_name
                        : "Search and select customer..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput 
                        placeholder="Search customer..." 
                        value={customerSearchTerm}
                        onValueChange={setCustomerSearchTerm}
                      />
                      <CommandEmpty>No customer found. Create customer first.</CommandEmpty>
                      <CommandGroup className="max-h-60 overflow-y-auto">
                        {filteredCustomers.map((customer) => (
                          <CommandItem
                            key={customer.id}
                            value={customer.company_name}
                            onSelect={() => handleCustomerSelect(customer.id)}
                          >
                            <Check
                              className={`mr-2 h-4 w-4 ${
                                formData.customer_id === customer.id ? "opacity-100" : "opacity-0"
                              }`}
                            />
                            <div>
                              <div className="font-medium">{customer.company_name}</div>
                              <div className="text-xs text-gray-500">{customer.contact_person} - {customer.mobile}</div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Service Type */}
              <div className="grid gap-2">
                <Label htmlFor="service_type">Service Type *</Label>
                <Select 
                  value={formData.service_type} 
                  onValueChange={handleServiceTypeChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select service type" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Contract Selection (for AMC/MMC Service) */}
              {formData.service_type === 'AMC/MMC Service' && formData.customer_id && (
                <div className="grid gap-2">
                  <Label htmlFor="contract_id">Select Contract *</Label>
                  {customerContracts.length === 0 ? (
                    <p className="text-sm text-orange-600">No active contracts found for this customer</p>
                  ) : (
                    <Select 
                      value={formData.contract_id} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, contract_id: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select contract" />
                      </SelectTrigger>
                      <SelectContent>
                        {customerContracts.map(contract => (
                          <SelectItem key={contract.id} value={contract.id}>
                            <div className="flex flex-col">
                              <span>{contract.contract_no} - {contract.contract_type}</span>
                              <span className="text-xs text-gray-500">
                                {contract.machine_device} | Services: {contract.services_used}/{contract.services_included} used
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* License Selection (for Stamping/License Renewal Service) */}
              {formData.service_type === 'Stamping/License Renewal Service' && formData.customer_id && (
                <div className="grid gap-2">
                  <Label htmlFor="license_id">Select Stamping/License (Expiring in 60 days) *</Label>
                  {customerLicenses.length === 0 ? (
                    <p className="text-sm text-orange-600">No licenses expiring in next 60 days for this customer</p>
                  ) : (
                    <Select 
                      value={formData.license_id} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, license_id: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select license" />
                      </SelectTrigger>
                      <SelectContent>
                        {customerLicenses.map(license => (
                          <SelectItem key={license.id} value={license.id}>
                            <div className="flex flex-col">
                              <span>{license.license_no} - {license.license_type}</span>
                              <span className="text-xs text-gray-500">
                                {license.machine_device} | Expires: {new Date(license.expiry_date).toLocaleDateString('en-IN')}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* Device Reference Number for In-Office Service */}
              {formData.service_type === 'In-Office Service' && (
                <div className="grid gap-2">
                  <Label htmlFor="device_reference_number">Device Reference Number *</Label>
                  <Input
                    id="device_reference_number"
                    value={formData.device_reference_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, device_reference_number: e.target.value.toUpperCase() }))}
                    placeholder="Enter unique device reference number (e.g., DEV-001)"
                    required
                  />
                  <p className="text-xs text-gray-500">This must be a unique reference number not used before</p>
                </div>
              )}

              {/* Description */}
              <div className="grid gap-2">
                <Label htmlFor="description">Service Description / Complaint *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the service requirement or complaint..."
                  rows={3}
                  required
                />
              </div>

              {/* Priority */}
              <div className="grid gap-2">
                <Label htmlFor="priority">Priority</Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Normal">Normal</SelectItem>
                    <SelectItem value="Urgent">Urgent</SelectItem>
                    <SelectItem value="Emergency">Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Due Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="due_date">Due Date *</Label>
                  <Input
                    id="due_date"
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="due_time">Due Time</Label>
                  <Input
                    id="due_time"
                    type="time"
                    value={formData.due_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, due_time: e.target.value }))}
                  />
                </div>
              </div>

              {/* Assigned Engineer */}
              <div className="grid gap-2">
                <Label htmlFor="assigned_to">
                  Assigned Engineer *
                  {user?.role === 'Service Engineer' && (
                    <span className="text-xs text-gray-500 ml-2">(Auto-assigned to you)</span>
                  )}
                </Label>
                <Select 
                  value={formData.assigned_to} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, assigned_to: value }))}
                  disabled={user?.role === 'Service Engineer'}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select engineer" />
                  </SelectTrigger>
                  <SelectContent>
                    {engineers.map(engineer => (
                      <SelectItem key={engineer.id} value={engineer.id}>
                        {engineer.name} - {engineer.mobile}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create Service'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Service Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Service Details - {selectedService?.service_no}</DialogTitle>
          </DialogHeader>
          {selectedService && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Customer</Label>
                  <p className="font-medium">{selectedService.customer?.company_name}</p>
                  {selectedService.customer?.mobile && (
                    <a href={`tel:${selectedService.customer.mobile}`} className="text-blue-600 hover:underline text-sm flex items-center gap-1 mt-1">
                      <Phone size={14} />
                      {selectedService.customer.mobile}
                    </a>
                  )}
                </div>
                <div>
                  <Label className="text-gray-500">Service Type</Label>
                  <p className="font-medium">{selectedService.service_type}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Priority</Label>
                  <p className={getPriorityColor(selectedService.priority)}>{selectedService.priority}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Status</Label>
                  <Badge className={getStatusBadgeVariant(selectedService.status)}>
                    {selectedService.status}
                  </Badge>
                  {selectedService.approval_status && selectedService.status === 'Closed' && (
                    <Badge variant="outline" className="ml-2">
                      {selectedService.approval_status}
                    </Badge>
                  )}
                </div>
                <div>
                  <Label className="text-gray-500">Due Date</Label>
                  <p className="font-medium">
                    {selectedService.due_date ? new Date(selectedService.due_date).toLocaleDateString('en-IN') : '-'}
                    {selectedService.due_time && ` ${selectedService.due_time}`}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Assigned Engineer</Label>
                  <p className="font-medium">{selectedService.assigned_engineer?.name || 'N/A'}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-500">Description</Label>
                <p className="mt-1 p-3 bg-gray-50 rounded">{selectedService.description}</p>
              </div>

              {selectedService.contract && (
                <div>
                  <Label className="text-gray-500">Contract</Label>
                  <p className="mt-1 p-3 bg-blue-50 rounded">
                    {selectedService.contract.contract_no} - {selectedService.contract.contract_type}
                    <br />
                    <span className="text-sm text-gray-500">
                      Services: {selectedService.contract.services_used}/{selectedService.contract.services_included} used
                    </span>
                  </p>
                </div>
              )}

              {selectedService.license && (
                <div>
                  <Label className="text-gray-500">Stamping/License</Label>
                  <p className="mt-1 p-3 bg-green-50 rounded">
                    {selectedService.license.license_no} - {selectedService.license.license_type}
                    <br />
                    <span className="text-sm text-gray-500">
                      Expires: {new Date(selectedService.license.expiry_date).toLocaleDateString('en-IN')}
                    </span>
                  </p>
                </div>
              )}

              {/* Device Reference Number for In-Office Service */}
              {selectedService.service_type === 'In-Office Service' && selectedService.device_reference_number && (
                <div className="p-4 bg-purple-50 rounded border border-purple-200">
                  <Label className="text-purple-700 font-semibold">Device Reference Number</Label>
                  <p className="mt-1 text-lg font-mono font-bold text-purple-800">
                    {selectedService.device_reference_number}
                  </p>
                </div>
              )}

              {/* Payment Info */}
              {selectedService.payment_status && (
                <div className="p-4 bg-yellow-50 rounded border border-yellow-200">
                  <Label className="text-gray-700 font-semibold">Payment Information</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <span className="text-gray-500 text-sm">Status:</span>
                      <Badge className="ml-2">{selectedService.payment_status}</Badge>
                    </div>
                    {selectedService.bill_type && (
                      <div>
                        <span className="text-gray-500 text-sm">Bill Type:</span>
                        <span className="ml-2 font-medium">{selectedService.bill_type}</span>
                      </div>
                    )}
                    {selectedService.payment_mode && (
                      <div>
                        <span className="text-gray-500 text-sm">Payment Mode:</span>
                        <span className="ml-2 font-medium">{selectedService.payment_mode}</span>
                      </div>
                    )}
                    {selectedService.service_charge && (
                      <div>
                        <span className="text-gray-500 text-sm">Amount:</span>
                        <span className="ml-2 font-medium">₹{selectedService.service_charge.toLocaleString('en-IN')}</span>
                      </div>
                    )}
                  </div>
                  {selectedService.payment_notes && (
                    <div className="mt-2">
                      <span className="text-gray-500 text-sm">Notes:</span>
                      <p className="text-sm mt-1">{selectedService.payment_notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Approval Info */}
              {selectedService.approval_status === 'Approved' && (
                <div className="p-4 bg-green-50 rounded border border-green-200">
                  <Label className="text-gray-700 font-semibold">Approval Information</Label>
                  <div className="mt-2 text-sm">
                    <p><span className="text-gray-500">Approved by:</span> {selectedService.approver?.name}</p>
                    <p><span className="text-gray-500">Approved at:</span> {formatIndianDate(selectedService.approved_at)}</p>
                    {selectedService.payment_reference && (
                      <p><span className="text-gray-500">Payment Reference:</span> {selectedService.payment_reference}</p>
                    )}
                    <p><span className="text-gray-500">Notes:</span> {selectedService.approval_notes}</p>
                  </div>
                </div>
              )}

              {/* Cancellation Info */}
              {selectedService.status === 'Canceled' && (
                <div className="p-4 bg-red-50 rounded border border-red-200">
                  <Label className="text-red-700 font-semibold">Cancellation Information</Label>
                  <div className="mt-2 text-sm">
                    <p><span className="text-gray-500">Cancelled by:</span> <span className="font-medium text-red-700">{selectedService.cancelled_by_name || 'N/A'}</span></p>
                    {selectedService.cancelled_at && (
                      <p><span className="text-gray-500">Cancelled at:</span> {formatIndianDate(selectedService.cancelled_at)}</p>
                    )}
                    {selectedService.cancellation_notes && (
                      <div className="mt-2">
                        <span className="text-gray-500">Reason:</span>
                        <p className="mt-1 text-red-700">{selectedService.cancellation_notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Comments */}
              {selectedService.comments && selectedService.comments.length > 0 && (
                <div>
                  <Label className="text-gray-500 flex items-center gap-1">
                    <MessageSquare size={14} />
                    Service Comments ({selectedService.comments.length})
                  </Label>
                  <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                    {selectedService.comments.map(comment => (
                      <div key={comment.id} className="p-3 bg-gray-50 rounded border-l-4 border-blue-400">
                        <p>{comment.comment}</p>
                        <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                          <span>By: {comment.created_by_name}</span>
                          <span>{formatIndianDate(comment.created_at)}</span>
                        </div>
                        {comment.next_visit_date && (
                          <p className="text-xs text-orange-600 mt-1">
                            <Clock size={12} className="inline mr-1" />
                            Next Visit: {new Date(comment.next_visit_date).toLocaleDateString('en-IN')}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Due Date Change History */}
              {selectedService.due_date_history?.length > 0 && (
                <div data-testid="due-date-history-section">
                  <Label className="text-gray-500 flex items-center gap-1">
                    <Clock size={14} />
                    Due Date History ({selectedService.due_date_history.length})
                  </Label>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                    {[...selectedService.due_date_history].reverse().map(h => (
                      <div key={h.id} className="p-2 bg-orange-50 rounded border-l-4 border-orange-400 text-sm" data-testid={`due-date-history-${h.id}`}>
                        <p>
                          <span className="text-gray-500 line-through">{h.old_due_date ? new Date(h.old_due_date).toLocaleDateString('en-IN') : 'None'}</span>
                          <span className="mx-2">→</span>
                          <span className="font-medium text-orange-700">{new Date(h.new_due_date).toLocaleDateString('en-IN')}</span>
                        </p>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>By: {h.changed_by_name || '-'} (via comment)</span>
                          <span>{formatIndianDate(h.changed_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Complaint Images - from linked complaint */}
              {selectedService.complaint_images && selectedService.complaint_images.length > 0 && (
                <div className="p-4 bg-amber-50 rounded border border-amber-200">
                  <Label className="text-amber-700 font-semibold flex items-center gap-2">
                    <Image size={14} />
                    Complaint Reference Images ({selectedService.complaint_images.length})
                  </Label>
                  <div className="mt-3 grid grid-cols-4 gap-2">
                    {selectedService.complaint_images.map((img, i) => (
                      <a 
                        key={i} 
                        href={img} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block border rounded overflow-hidden hover:ring-2 hover:ring-amber-400 transition-all"
                      >
                        <img 
                          src={img} 
                          alt={`Complaint image ${i + 1}`} 
                          className="w-full h-20 object-cover"
                          onError={(e) => { e.target.src = '/placeholder-image.png'; e.target.onerror = null; }}
                        />
                      </a>
                    ))}
                  </div>
                  {selectedService.complaint_id && (
                    <p className="text-xs text-amber-700 mt-2">
                      Linked to Complaint ID: {selectedService.complaint_id}
                    </p>
                  )}
                </div>
              )}

              <div className="text-sm text-gray-500">
                Created: {formatIndianDate(selectedService.created_at)}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={updateStatusDialogOpen} onOpenChange={setUpdateStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Service Status</DialogTitle>
            <DialogDescription asChild>
              <div className="flex items-center gap-2">
                Current status: <Badge>{selectedService?.status}</Badge>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">Select new status for {selectedService?.service_no}:</p>
            <div className="grid grid-cols-2 gap-3">
              {selectedService?.status === 'New' && (
                <Button onClick={() => handleStatusUpdate('On Process')} className="bg-blue-600">
                  Start Processing
                </Button>
              )}
              {(selectedService?.status === 'New' || selectedService?.status === 'On Process') && (
                <>
                  <Button onClick={() => handleStatusUpdate('Closed')} className="bg-green-600">
                    Close Service
                  </Button>
                  <Button onClick={() => handleStatusUpdate('Canceled')} variant="destructive">
                    Cancel Service
                  </Button>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateStatusDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service Closure Dialog */}
      <Dialog open={closureDialogOpen} onOpenChange={setClosureDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Close Service</DialogTitle>
            <DialogDescription>
              Complete service closing for {selectedService?.service_no}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleClosureSubmit}>
            <div className="space-y-4 py-4">
              {/* Step 1: Service Type Category */}
              <div className="grid gap-2">
                <Label>Service Type *</Label>
                <Select 
                  value={closureData.service_type_category} 
                  onValueChange={(value) => setClosureData(prev => ({ 
                    ...prev, 
                    service_type_category: value,
                    payment_status: '',
                    payment_mode: '',
                    free_service_notes: '',
                    service_charge: '',
                    due_date: ''
                  }))}
                >
                  <SelectTrigger data-testid="service-type-category-select">
                    <SelectValue placeholder="Select service type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Paid">💰 Paid Service</SelectItem>
                    <SelectItem value="Free">🆓 Free Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* FREE SERVICE: Ask Notes */}
              {closureData.service_type_category === 'Free' && (
                <div className="grid gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                  <Label className="text-green-800">Free Service Notes *</Label>
                  <Textarea
                    value={closureData.free_service_notes}
                    onChange={(e) => setClosureData(prev => ({ ...prev, free_service_notes: e.target.value }))}
                    placeholder="Enter reason/notes for free service..."
                    rows={3}
                    required
                    data-testid="free-service-notes"
                  />
                  <p className="text-xs text-green-600">This service will be marked as free and sent for approval.</p>
                </div>
              )}

              {/* PAID SERVICE: Payment Status */}
              {closureData.service_type_category === 'Paid' && (
                <>
                  <div className="grid gap-2">
                    <Label>Payment Status *</Label>
                    <Select 
                      value={closureData.payment_status} 
                      onValueChange={(value) => setClosureData(prev => ({ 
                        ...prev, 
                        payment_status: value,
                        payment_mode: '',
                        due_date: ''
                      }))}
                    >
                      <SelectTrigger data-testid="payment-status-select">
                        <SelectValue placeholder="Select payment status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Paid">✅ Paid</SelectItem>
                        <SelectItem value="Pending">⏳ Pending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Service Charge (always required for paid) */}
                  <div className="grid gap-2">
                    <Label>Service Charge (₹) *</Label>
                    <Input
                      type="number"
                      value={closureData.service_charge}
                      onChange={(e) => setClosureData(prev => ({ ...prev, service_charge: e.target.value }))}
                      placeholder="Enter amount"
                      required
                      min="1"
                      data-testid="service-charge-input"
                    />
                  </div>

                  {/* IF PAID: Payment Mode + Collected By */}
                  {closureData.payment_status === 'Paid' && (
                    <div className="space-y-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="grid gap-2">
                        <Label className="text-blue-800">Payment Mode *</Label>
                        <Select 
                          value={closureData.payment_mode} 
                          onValueChange={(value) => setClosureData(prev => ({ ...prev, payment_mode: value }))}
                        >
                          <SelectTrigger data-testid="payment-mode-select">
                            <SelectValue placeholder="Select payment mode" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Cash">💵 Cash</SelectItem>
                            <SelectItem value="UPI">📱 UPI</SelectItem>
                            <SelectItem value="Bank Transfer">🏦 Bank Transfer</SelectItem>
                            <SelectItem value="Cheque">📄 Cheque</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid gap-2">
                        <Label className="text-blue-800">Payment Collected By</Label>
                        <Select 
                          value={closureData.collected_by} 
                          onValueChange={(value) => setClosureData(prev => ({ ...prev, collected_by: value }))}
                          data-testid="collected-by-select"
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select who collected payment" />
                          </SelectTrigger>
                          <SelectContent>
                            {engineers.map(eng => (
                              <SelectItem key={eng.id} value={eng.id}>
                                {eng.name} ({eng.role})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* IF PENDING: Due Date */}
                  {closureData.payment_status === 'Pending' && (
                    <div className="space-y-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="grid gap-2">
                        <Label className="text-orange-800">Payment Due Date *</Label>
                        <Input
                          type="date"
                          value={closureData.due_date}
                          onChange={(e) => setClosureData(prev => ({ ...prev, due_date: e.target.value }))}
                          required
                          min={new Date().toISOString().split('T')[0]}
                          data-testid="due-date-input"
                        />
                      </div>
                      <p className="text-xs text-orange-600">Payment will be tracked until this due date.</p>
                    </div>
                  )}

                  {/* Notes */}
                  <div className="grid gap-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={closureData.payment_notes}
                      onChange={(e) => setClosureData(prev => ({ ...prev, payment_notes: e.target.value }))}
                      placeholder="Enter any additional notes..."
                      rows={2}
                      data-testid="payment-notes"
                    />
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setClosureDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading || !closureData.service_type_category} 
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? 'Closing...' : 'Close Service'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {approvalData.action === 'approve' ? 'Approve Service' : 'Reject Service'}
            </DialogTitle>
            <DialogDescription>
              {selectedService?.service_no} - {selectedService?.customer?.company_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleApprovalSubmit}>
            <div className="space-y-4 py-4">
              {/* Payment Reference (required for On Bill) */}
              {approvalData.action === 'approve' && selectedService?.bill_type === 'On Bill' && (
                <div className="grid gap-2">
                  <Label>Payment Reference Number *</Label>
                  <Input
                    value={approvalData.payment_reference}
                    onChange={(e) => setApprovalData(prev => ({ ...prev, payment_reference: e.target.value }))}
                    placeholder="Enter payment/invoice reference"
                    required
                  />
                </div>
              )}

              {/* Notes */}
              <div className="grid gap-2">
                <Label>{approvalData.action === 'approve' ? 'Approval Notes *' : 'Rejection Reason *'}</Label>
                <Textarea
                  value={approvalData.approval_notes}
                  onChange={(e) => setApprovalData(prev => ({ ...prev, approval_notes: e.target.value }))}
                  placeholder={approvalData.action === 'approve' ? 'Enter approval notes...' : 'Enter reason for rejection...'}
                  rows={3}
                  required
                />
              </div>

              {approvalData.action === 'reject' && (
                <p className="text-sm text-orange-600">
                  Note: Rejecting will send the service back to the engineer for correction.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setApprovalDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                className={approvalData.action === 'approve' ? 'bg-green-600' : 'bg-red-600'}
              >
                {loading ? 'Processing...' : (approvalData.action === 'approve' ? 'Approve' : 'Reject')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Comment Dialog */}
      <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Service Comment</DialogTitle>
            <DialogDescription>
              Add notes or schedule next visit for {selectedService?.service_no}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddComment}>
            <div className="space-y-4 py-4">
              <div className="grid gap-2">
                <Label>Comment *</Label>
                <Textarea
                  value={commentData.comment}
                  onChange={(e) => setCommentData(prev => ({ ...prev, comment: e.target.value }))}
                  placeholder="Enter your comment or service notes..."
                  rows={4}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label>Next Visit Date (if service not completed)</Label>
                <Input
                  type="date"
                  value={commentData.next_visit_date}
                  onChange={(e) => setCommentData(prev => ({ ...prev, next_visit_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="comment-next-visit-date"
                />
                <p className="text-xs text-gray-500">Setting this also moves the service due date to this day (change is logged).</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCommentDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} data-testid="comment-submit-btn">
                {loading ? 'Adding...' : 'Add Comment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel Service Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Cancel Service</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel service {selectedService?.service_no}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-800">
                <AlertTriangle size={16} className="inline mr-2" />
                Cancellation notes are mandatory.
              </p>
            </div>
            <div className="grid gap-2">
              <Label>Cancellation Notes *</Label>
              <Textarea
                value={cancelNotes}
                onChange={(e) => setCancelNotes(e.target.value)}
                placeholder="Enter reason for cancellation..."
                rows={4}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCancelDialogOpen(false)}>
              Go Back
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancelService}
              disabled={loading || !cancelNotes.trim()}
            >
              {loading ? 'Cancelling...' : 'Confirm Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Engineer Dialog */}
      <Dialog open={changeEngineerDialogOpen} onOpenChange={setChangeEngineerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Service Engineer</DialogTitle>
            <DialogDescription>
              Change assigned engineer for service {selectedService?.service_no}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <UserCog size={16} className="inline mr-2" />
                Current Engineer: <strong>{selectedService?.assigned_engineer?.name || 'Not Assigned'}</strong>
              </p>
            </div>
            <div className="grid gap-2">
              <Label>Select New Engineer *</Label>
              <Select value={newEngineerId} onValueChange={setNewEngineerId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select engineer..." />
                </SelectTrigger>
                <SelectContent>
                  {engineers.map(eng => (
                    <SelectItem key={eng.id} value={eng.id}>
                      {eng.name} - {eng.mobile}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Reason for Change (Optional)</Label>
              <Textarea
                value={changeEngineerReason}
                onChange={(e) => setChangeEngineerReason(e.target.value)}
                placeholder="Enter reason for changing engineer..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setChangeEngineerDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleChangeEngineer}
              disabled={loading || !newEngineerId}
            >
              {loading ? 'Changing...' : 'Change Engineer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ExportPasswordDialog {...protectedExport} />
    </div>
  );
};

// Separate component for service list card
const ServiceListCard = ({ 
  services, 
  loading, 
  searchTerm, 
  setSearchTerm, 
  statusFilter, 
  setStatusFilter, 
  typeFilter, 
  setTypeFilter, 
  engineerFilter,
  setEngineerFilter,
  priorityFilter,
  setPriorityFilter,
  expiredOnly,
  setExpiredOnly,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  engineers,
  serviceTypes,
  handleViewService,
  handleOpenStatusUpdate,
  handleOpenCommentDialog,
  handleChangeEngineer,
  canUpdateStatus,
  canChangeEngineer,
  isOverdue,
  getPriorityColor,
  getStatusBadgeVariant,
  user
}) => {
  // Pagination state
  const [currentPage, setCurrentPage] = React.useState(1);
  const recordsPerPage = 250;

  // Pagination logic
  const totalPages = Math.ceil(services.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentServices = services.slice(indexOfFirstRecord, indexOfLastRecord);

  // Reset to page 1 when services change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [services.length]);

  return (
  <Card>
    <CardHeader>
      <CardTitle>Services</CardTitle>
      <CardDescription>View and manage all service bookings</CardDescription>
      <div className="flex flex-wrap gap-3 mt-4">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search services..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-44">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {serviceTypes.map(type => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-36">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="New">New</SelectItem>
            <SelectItem value="On Process">On Process</SelectItem>
            <SelectItem value="Closed">Closed</SelectItem>
            <SelectItem value="Canceled">Canceled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-full sm:w-36">
            <SelectValue placeholder="All Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            <SelectItem value="Emergency">Emergency</SelectItem>
            <SelectItem value="Urgent">Urgent</SelectItem>
            <SelectItem value="Normal">Normal</SelectItem>
          </SelectContent>
        </Select>
        {(user?.role === 'Admin' || user?.role === 'Back Office Executive') && (
          <Select value={engineerFilter} onValueChange={setEngineerFilter}>
            <SelectTrigger className="w-full sm:w-44">
              <SelectValue placeholder="All Engineers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Engineers</SelectItem>
              {engineers.map(eng => (
                <SelectItem key={eng.id} value={eng.id}>{eng.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {/* Date Range Filter */}
      <div className="flex flex-wrap gap-3 mt-3 items-center">
        <Button
          type="button"
          size="sm"
          variant={expiredOnly ? 'destructive' : 'outline'}
          onClick={() => setExpiredOnly(!expiredOnly)}
          className={expiredOnly ? '' : 'border-red-300 text-red-700 hover:bg-red-50'}
          data-testid="expired-due-filter-btn"
        >
          <AlertTriangle size={14} className="mr-1" />
          {expiredOnly ? 'Showing Expired Due Date' : 'Expired Due Date'}
          {expiredOnly && <XCircle size={14} className="ml-2" />}
        </Button>
        <div className={`flex items-center gap-2 ${expiredOnly ? 'opacity-50 pointer-events-none' : ''}`}>
          <Label className="text-sm text-gray-600 whitespace-nowrap">From:</Label>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-full sm:w-40"
          />
        </div>
        <div className={`flex items-center gap-2 ${expiredOnly ? 'opacity-50 pointer-events-none' : ''}`}>
          <Label className="text-sm text-gray-600 whitespace-nowrap">To:</Label>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-full sm:w-40"
          />
        </div>
        <span className="text-xs text-gray-500" data-testid="service-filter-hint">
          {expiredOnly ? 'Due date passed & not closed — date range ignored' : '(Default: 30 days before to 30 days after)'}
        </span>
      </div>
    </CardHeader>
    <CardContent>
      {loading && !services.length ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : services.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Wrench size={48} className="mx-auto mb-4 text-gray-300" />
          <p>No services found</p>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="block md:hidden space-y-4">
            {currentServices.map((service) => (
              <div 
                key={service.id}
                className={`border rounded-lg p-4 ${isOverdue(service.due_date, service.status) ? 'bg-red-50 border-red-200' : 'bg-white'}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="font-semibold text-blue-600">{service.service_no}</div>
                    <div className="text-sm text-gray-600">{service.customer?.company_name || 'N/A'}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className={getStatusBadgeVariant(service.status)}>
                      {service.status}
                    </Badge>
                    {service.status === 'Closed' && service.approval_status === 'Pending' && (
                      <Badge variant="outline" className="text-xs bg-yellow-50">
                        Pending Approval
                      </Badge>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  <div>
                    <span className="text-gray-500">Type:</span>
                    <Badge variant="outline" className="ml-2 text-xs">
                      {service.service_type}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-gray-500">Priority:</span>
                    <Badge className={`ml-2 ${getPriorityColor(service.priority)}`}>
                      {service.priority}
                    </Badge>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500">Engineer:</span>
                    <span className="ml-2">{service.assigned_engineer?.name || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Created:</span>
                    <span className="ml-2">{service.created_at ? new Date(service.created_at).toLocaleDateString('en-IN') : '-'}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500">Due:</span>
                    {isOverdue(service.due_date, service.status) && (
                      <AlertTriangle size={14} className="text-red-500 ml-2" />
                    )}
                    <span className={`ml-2 ${isOverdue(service.due_date, service.status) ? 'text-red-600 font-medium' : ''}`}>
                      {service.due_date ? new Date(service.due_date).toLocaleDateString('en-IN') : '-'}
                      {service.due_time && ` ${service.due_time}`}
                    </span>
                  </div>
                </div>
                
                <div className="flex gap-2 pt-2 border-t">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="flex-1"
                    onClick={() => handleViewService(service)}
                  >
                    <Eye size={14} className="mr-1" />
                    View
                  </Button>
                  {canChangeEngineer && service.status !== 'Closed' && service.status !== 'Canceled' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleChangeEngineer(service)}
                    >
                      <UserCog size={14} />
                    </Button>
                  )}
                  {canUpdateStatus(service) && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleOpenStatusUpdate(service)}
                      >
                        <Edit size={14} />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleOpenCommentDialog(service)}
                      >
                        <MessageSquare size={14} />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Service No.</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Created Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Engineer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentServices.map((service) => (
                  <TableRow 
                    key={service.id}
                    className={`
                      ${isOverdue(service.due_date, service.status) ? 'bg-red-50' : ''}
                      ${service.backoffice_update_seen === false ? 'border-l-4 border-l-blue-500 bg-blue-50/30' : ''}
                    `}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {service.service_no}
                        {service.backoffice_update_seen === false && (
                          <Badge className="bg-blue-500 text-white text-xs animate-pulse">Updated</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{service.customer?.company_name || 'N/A'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {service.service_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getPriorityColor(service.priority)}>
                        {service.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-600">
                        {service.created_at ? new Date(service.created_at).toLocaleDateString('en-IN') : '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {isOverdue(service.due_date, service.status) && (
                          <AlertTriangle size={14} className="text-red-500" />
                        )}
                        <span className={isOverdue(service.due_date, service.status) ? 'text-red-600' : ''}>
                          {service.due_date ? new Date(service.due_date).toLocaleDateString('en-IN') : '-'}
                        </span>
                        {service.due_time && (
                          <span className="text-gray-500 text-xs ml-1">{service.due_time}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{service.assigned_engineer?.name || 'N/A'}</TableCell>
                    <TableCell>
                      <Badge className={getStatusBadgeVariant(service.status)}>
                        {service.status}
                      </Badge>
                      {service.status === 'Closed' && service.approval_status === 'Pending' && (
                        <Badge variant="outline" className="ml-1 text-xs bg-yellow-50">
                          Pending Approval
                        </Badge>
                      )}
                      {service.status === 'Closed' && service.approval_status === 'Approved' && (
                        <Badge variant="outline" className="ml-1 text-xs bg-green-50 text-green-700">
                          Approved
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleViewService(service)}
                        data-testid={`view-service-${service.id}`}
                      >
                        <Eye size={16} />
                      </Button>
                      {canChangeEngineer && service.status !== 'Closed' && service.status !== 'Canceled' && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleChangeEngineer(service)}
                          title="Change Engineer"
                        >
                          <UserCog size={16} />
                        </Button>
                      )}
                      {canUpdateStatus(service) && (
                        <>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleOpenStatusUpdate(service)}
                            title="Update Status"
                          >
                            <Edit size={16} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleOpenCommentDialog(service)}
                            title="Add Comment"
                            data-testid={`add-comment-${service.id}`}
                          >
                            <MessageSquare size={16} />
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          {services.length > 0 && (
            <div className="flex items-center justify-between mt-4 px-2">
              <div className="text-sm text-gray-600">
                Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, services.length)} of {services.length} services
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm px-3">
                  Page {currentPage} of {totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </CardContent>
  </Card>
  );
};

export default ServiceManager;
